import { useEffect, useRef, useState } from "react";
import * as WebChat from "botframework-webchat";

import askEMMAApiService from "../../../services/AskEMMAApiService";

import GLOBAL from "../../../global";
import { useTenantFeatures } from "../../../features/tenantFeatures/useTenantFeature";
import { useDispatch, useSelector } from "react-redux";
import { resetEventData, setShowEmma } from "../../../features/askEmma/askEmmaSlice";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import routes from "../../../constants/Routes";

const { REACT_APP_BASE_API_URL, REACT_APP_VA_API_URL } = process.env;

// Custom DirectLine token endpoint for new-registration page
const NEW_REGISTRATION_DIRECT_LINE_ENDPOINT = "https://014141e063564da89a7cbeed15f383.10.environment.api.powerplatform.com/powervirtualagents/botsbyschema/crf13_askEmmaSalesAgent/directline/token?api-version=2022-03-01-preview";
const providerApi = REACT_APP_BASE_API_URL + "/api/";
const vaApi = REACT_APP_VA_API_URL + "/api/";

export const useWebChat = () => {
    const webChatRef = useRef(null);
    const [directLine, setDirectLine] = useState(null);
    const doesAlreadyInitializedRef = useRef(false);

    const askEmmaState = useSelector(state => state.askEmma);
    const dispatch = useDispatch();
    const location = useLocation();
    const eventName = askEmmaState.eventName || '';
    const testId = askEmmaState.testId || '';
    const defaultAskEmmaAvatarImage = 'https://va-mmh-development.azurewebsites.net/content/logos/logo-transparent-sm.png';
    const { askEmmaAvatarImage } = useTenantFeatures();
    const resolvedAskEmmaAvatarImage = askEmmaAvatarImage || defaultAskEmmaAvatarImage;

    const styleOptions = {
        hideUploadButton: true,
        accent: "#00809d",
        botAvatarBackgroundColor: "#FFFFFF",
        // botAvatarImage: "/content/logos/logo-transparent-sm.png",
        botAvatarImage: resolvedAskEmmaAvatarImage,
        botAvatarInitials: "BT",
        backgroundColor: "#EFF9FF;",
    };
    const locale = document.documentElement.lang || "en";
    // const testId = "";
    const tenant = GLOBAL.TENANT;
    const patientId = (GLOBAL?.USER_INFO && GLOBAL?.USER_INFO?.['ContactId']) || "";
    // const providerApi = "https://stgapi.injurycloud.com/api/"; // Replace with dynamic value if needed
    // const vaApi = "https://devvaapi.injurycloud.com/api/"; // Replace with dynamic value if needed
    const SECOND = 1000;
    const MINUTE = 60 * SECOND;
    const INTERVAL_TIME = 3 * MINUTE;


    useEffect(() => {
        initializeWebChat();

        const intervalId = setInterval(() => {
            fetchRiskTrigger();
        }, INTERVAL_TIME);
        return () => {
            clearInterval(intervalId);
        };
    }, [locale, styleOptions]);

    useEffect(() => {
        if (directLine && eventName) {
            triggerEvent(eventName);
        }
    }, [directLine, eventName]);


    const fetchRiskTrigger = async () => {
        const response = await askEMMAApiService.fetchRiskTrigger(testId);

        if (!response.success) {
            toast.error("Failed to fetch risk trigger.");
            return;
        }

        const triggerName = response.data?.TriggerName || '';
        const triggerId = response.data?.TriggerId || '';
        const baseQuestionId = response.data?.BaseQuestionId || '';

        if (triggerName !== '' && triggerName !== undefined) {
            directLine
                .postActivity({
                    type: 'event',
                    name: triggerName,
                    timestamp: new Date().toISOString(),
                    channelData: {
                        TestId: testId,
                        Tenant: tenant,
                        ProviderApi: providerApi,
                        VaApi: vaApi,
                        FileUrl: '',
                        PatientId: (GLOBAL.USER_INFO && GLOBAL.USER_INFO['ContactId']) || '',
                        ProviderId: '',
                        TriggerName: triggerName,
                        TriggerId: triggerId,
                        BaseQuestionId: baseQuestionId,
                        ...(location.pathname === routes.newRegistration || location.pathname === routes.onboarding ?
                            {
                                askEmmaSource: 'patientportal',
                                askEmmaRole: 'patient',
                            } :
                            {}
                        ),
                    }

                })
                .subscribe(
                    id => console.log('Minute tick event sent successfully with ID ', id),
                    error => console.error('Error sending event: ', error)
                );
        }
    }

    const initializeWebChat = async () => {
        if (doesAlreadyInitializedRef.current) {
            return;
        }
        try {

            let token = "";
            let conversationId = "";

            const conversationInfoResponse = await askEMMAApiService.getConversationInfo();
            if (conversationInfoResponse.success) {
                token = conversationInfoResponse.data?.Token;
                conversationId = conversationInfoResponse.data?.ConversationId;
            }

            if (!token) {
                const isNewRegistrationPage = location.pathname === routes.newRegistration;
                const isOnboardingPage = location.pathname === routes.onboarding;
                const tokenEndPoint = (isNewRegistrationPage || isOnboardingPage) ? NEW_REGISTRATION_DIRECT_LINE_ENDPOINT : null;
                const response = await askEMMAApiService.getDirectLineToken(tokenEndPoint);
                if (!response.success) {
                    throw new Error("Failed to retrieve Direct Line token.");
                }
                token = response.data?.token;
            }


            const directLineOptions = { token };

            if (conversationId) {
                directLineOptions.conversationId = conversationId;
            }


            const tempDirectLine = WebChat.createDirectLine(directLineOptions);
            setDirectLine(tempDirectLine);

            tempDirectLine.connectionStatus$.subscribe(async status => {
                if (status === 2) {
                    conversationId = tempDirectLine.conversationId;
                    const responseSavingInfo = await askEMMAApiService.saveConversationInfo(conversationId, token);
                    if (!responseSavingInfo.success) {
                        console.error('Cannot save conversation info');
                    }
                } else if (status === 4 || status === 5) {
                    const isNewRegistrationPage = location.pathname === routes.newRegistration;
                    const isOnboardingPage = location.pathname === routes.onboarding;
                    const tokenEndPoint = (isNewRegistrationPage || isOnboardingPage) ? NEW_REGISTRATION_DIRECT_LINE_ENDPOINT : null;
                    const refreshResponse = askEMMAApiService.getDirectLineToken(tokenEndPoint);
                    if (refreshResponse.success) {
                        tempDirectLine.refreshToken(refreshResponse.data?.token);
                        const responseSavingInfo = await askEMMAApiService.saveConversationInfo(conversationId, token);
                        if (!responseSavingInfo.success) {
                            console.error('Cannot save conversation info');
                        }
                    }
                    else {
                        console.error('Failed to refresh Direct Line token.');
                    }
                }
            });

            const subscription = tempDirectLine.connectionStatus$.subscribe({
                next(value) {
                    if (value === 2) {
                        tempDirectLine
                            .postActivity({
                                type: 'event',
                                name: 'startConversation',
                                timestamp: new Date().toISOString(),
                                channelData: {
                                    TestId: testId,
                                    Tenant: tenant,
                                    ProviderApi: providerApi,
                                    VaApi: vaApi,
                                    FileUrl: '',
                                    PatientId: '',
                                    ProviderId: '',
                                    ...(location.pathname === routes.newRegistration || location.pathname === routes.onboarding ?
                                            {
                                                askEmmaSource: 'patientportal',
                                                askEmmaRole: 'patient',
                                            } :
                                            {}
                                    ),
                                }
                            })
                            .subscribe(
                                id => {
                                    // console.log('Activity sent successfully with ID ', id);
                                    const startConversationEvent = new Event('startConversation');
                                    dispatchEvent(startConversationEvent);
                                },
                                error => console.error('Error sending activity: ', error)
                            );

                        subscription.unsubscribe();
                    }
                }
            });

            // Listen to incoming activities
            // directLine.activity$.subscribe((activity) => {
            //     if (activity.type === "message") {
            //         console.log("Received message:", activity);
            //     }
            // });

            // Render the WebChat component - MOVED TO WebChatComponent
            // WebChat.renderWebChat({ directLine: tempDirectLine, locale, styleOptions }, document.getElementById('webchat'));
            // const directLineInitializedEvent = new Event('directLineInitialized');
            // dispatchEvent(directLineInitializedEvent);

        } catch (error) {
            console.error("Error initializing WebChat:", error);
        }

        doesAlreadyInitializedRef.current = true;
    };

    const sendMessage = (text, fileUrl) => {
        const activity = {
            from: { id: "", name: "" },
            type: "message",
            attachments: [],
            timestamp: new Date().toISOString(),
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: locale,
            channelData: {
                TestId: testId,
                Tenant: tenant,
                ProviderApi: providerApi,
                VaApi: vaApi,
                FileUrl: fileUrl || "",
                PatientId: patientId,
                ProviderId: "",
                ...(location.pathname === routes.newRegistration || location.pathname === routes.onboarding ?
                        {
                            askEmmaSource: 'patientportal',
                            askEmmaRole: 'patient',
                        } :
                        {}
                ),
            },
            properties: {
                customProperty: "customValue",
            },
        };

        if (text) {
            activity.text = text;
            activity.name = "";
        }

        if (fileUrl) {
            activity.name = "FileUploadTrigger";
            activity.channelData.FileUrl = fileUrl;
        }

        directLine
            .postActivity(activity)
            .subscribe(
                // (id) => console.log("Activity sent successfully with ID ", id),
                (error) => console.error("Error sending activity: ", error)
            );
    };

    const triggerEvent = (triggerName) => {
        directLine
            .postActivity({
                type: 'event',
                name: triggerName,
                timestamp: new Date().toISOString(), // Example: send current timestamp.
                channelData: {
                    Tenant: tenant,
                    ProviderApi: providerApi,
                    VaApi: vaApi,
                    FileUrl: '',
                    PatientId: patientId,
                    ProviderId: '',
                    TriggerName: triggerName,
                    TriggerId: 0,
                    BaseQuestionId: 0,
                    TestId: testId,
                    ...(location.pathname === routes.newRegistration || location.pathname === routes.onboarding ?
                            {
                                askEmmaSource: 'patientportal',
                                askEmmaRole: 'patient',
                            } :
                            {}
                    ),
                }
            })
            .subscribe(
                id => console.log('Minute tick event sent successfully with ID ', id),
                error => console.error('Error sending event: ', error)
            );

        dispatch(setShowEmma(true));
        // dispatch(resetEventData());
    };

    return { webChatRef, directLine, styleOptions, locale, sendMessage };
};
